/**
 * Timer → Gjennomgang: the month-close review (plan 0026 "Timeliste as the month-close workbench",
 * redesigned as the Timer workspace 2026-10-09). One list of registrations in the chosen month or
 * week, filtered by person and status, each opening its details beside the list. The weekly
 * matrix ("Månedsoversikt") stays as a second layout. timeliste/close.ts decides what every shift
 * is, timeliste/review.ts adds overlap and invalid time, so counts, list and bulk rule agree.
 *
 * Approval is PUT /admin/shifts/:id/accept per shift (the API has no bulk route), which refuses a
 * shift without both punches. "Se N klare" first narrows the list to the ready shifts; only then
 * does it offer to approve the ready ones on the visible page, after a confirmation. A missing,
 * open, overlapping or invalid registration is never in a bulk approval. Correcting (the shift
 * drawer) and approving stay separate actions.
 *
 * Addresses kept: ?mnd=YYYY-MM (Timer til lønn, Hjem), ?uke=YYYY-MM-DD (Hjem: that week),
 * ?vis=handling|avvik|uten|klare|godkjent|alle (the status filter), ?ny=registrering (opens «Legg til
 * registrering» once, for whoever may correct; the welcome overview, #130).
 */
import { errorText } from '@/api/client';
import { type ApiShift, useAbsences, useShiftTypes, useShiftWrites, useShifts, useUsers } from '@/api/data';
import { projectLabel, useProjectWord, useProjects, useProjectsOn } from '@/api/projects';
import { useCan } from '@/auth/caps';
import { T, useT } from '@/i18n';
import { type AbsenceDraft, AbsenceDrawer, newAbsenceDraft } from '@/pages/fravaer/AbsenceDrawer';
import {
  breakPolicy,
  dayEntries,
  oslo,
  rulePerson,
  ruleText,
  tenantRules,
  today,
  worked,
  workedResult,
  worst,
} from '@/rules';
import { useTenant } from '@/shell/tenant';
import { Button, Empty, LoadError, Menu, Skeleton, Tip, toast } from '@/ui';
import { PeriodControl } from '@/ui/PeriodControl';
import { PersonLabel } from '@/ui/PersonLabel';
import { DN, fmtD, fmtDs, fullName, monthLabel, num, typeName } from '@/ui/format';
import { Icon } from '@/ui/icons';
import { DetailPanel, Fact, Notice, Status, TabPanel } from '@/ui/page';
import { addDays, dow, inRange, isoWeek, mondayOf } from '@timefly/core';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { TimerHead } from './timeliste/TimerHead';
import { useApproveMany } from './timeliste/approve';
import { type Kind, countKinds, deviationShort, deviationText, kindOf, needsAction } from './timeliste/close';
import { type Block, blocksOf, bulkReady, pickable, splitParts } from './timeliste/review';
import { WORKLISTS, inWorklist, worklistOf } from './timeliste/worklist';
import { type DraftShift, type EditFocus, ShiftDrawer, draftOf, newDraft } from './vaktplan/ShiftDrawer';

const dateOf = (s: ApiShift) => oslo(s.start_time)?.date ?? '';
/** one person's day, the key a split (#115) is grouped on */
const dayKeyOf = (s: ApiShift) => `${s.user_id}:${dateOf(s)}`;
const clock = (iso: string | null | undefined) => oslo(iso)?.time?.slice(0, 5) ?? '';
/** "1 vakt", "2 vakter" */
const vakter = (n: number) => `${n} ${n === 1 ? T('vakt') : T('vakter')}`;
const regs = (n: number) => T('{n, plural, one {# registrering} other {# registreringer}}', { n });
const PAGE = 25;
/**
 * The clock-out's day when it is not the clock-in's: «neste dag» for the next calendar day in Oslo
 * only, else the date itself, so a clock-out pressed days late never reads as the next morning
 * (#149 PW2/PAY-04).
 */
const outDay = (s: ApiShift): string | null => {
  const a = oslo(s.punch_in_time)?.date;
  const b = oslo(s.punch_out_time)?.date;
  if (!a || !b || a === b) return null;
  return b === addDays(a, 1) ? T('neste dag') : T('til {day}', { day: fmtD(b) });
};

type Filter = 'handling' | 'avvik' | 'uten' | 'klare' | 'godkjent' | 'alle';
const FILTERS: Filter[] = ['handling', 'avvik', 'uten', 'klare', 'godkjent', 'alle'];
const FILTER_TEXT: Record<Filter, string> = {
  handling: 'Trenger vurdering',
  avvik: 'Avvik fra plan',
  uten: 'Uten stempling',
  klare: 'Klare til godkjenning',
  godkjent: 'Godkjent',
  alle: 'Alle',
};
const inFilter = (f: Filter, k: Kind): boolean =>
  f === 'handling'
    ? needsAction(k)
    : f === 'avvik'
      ? k === 'deviation'
      : f === 'uten'
        ? k === 'missing' || k === 'open'
        : f === 'klare'
          ? k === 'ready'
          : f === 'godkjent'
            ? k === 'approved'
            : true;

const monthOf = (d: string) => d.slice(0, 7);
const firstOf = (m: string) => `${m}-01`;
const lastOf = (m: string) => {
  const [y, mo] = m.split('-').map(Number) as [number, number];
  return `${m}-${String(new Date(Date.UTC(y, mo, 0)).getUTCDate()).padStart(2, '0')}`;
};
const stepMonth = (m: string, by: number) => {
  const [y, mo] = m.split('-').map(Number) as [number, number];
  const d = new Date(Date.UTC(y, mo - 1 + by, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};

/** The status label a row carries: the words carry the meaning. */
function statusOf(
  k: Kind,
  block: Block | undefined,
): { tone: 'ok' | 'warn' | 'dgr' | 'neu' | 'act'; text: string } {
  if (block === 'invalid') return { tone: 'dgr', text: T('Ugyldig tid') };
  if (block === 'overlap') return { tone: 'dgr', text: T('Overlapper en annen registrering') };
  switch (k) {
    case 'missing':
      return { tone: 'warn', text: T('Mangler stempling') };
    case 'open':
      return { tone: 'warn', text: T('Mangler utstempling') };
    case 'deviation':
      return { tone: 'warn', text: T('Avvik fra plan') };
    case 'ready':
      return { tone: 'act', text: T('Klar') };
    case 'approved':
      return { tone: 'ok', text: T('Godkjent') };
    case 'running':
      return { tone: 'neu', text: T('Pågår') };
    default:
      return { tone: 'neu', text: T('Planlagt') };
  }
}

export default function Timeliste() {
  const t = useT();
  const can = useCan();
  // approving is a permission of its own; without it the page reads, it does not approve
  const mayApprove = can('timeliste.godkjenne');
  const { tenant } = useTenant();
  const [sp, setSp] = useSearchParams();
  const edit = (f: (n: URLSearchParams) => void) => {
    const n = new URLSearchParams(sp);
    f(n);
    if (['mnd', 'uke'].some((key) => n.get(key) !== sp.get(key))) {
      n.delete('arbeidsliste');
      n.delete('valgt');
      n.delete('vakt');
    }
    setSp(n, { replace: true });
  };
  // Hjem and Innboks link here with the week of the problem they counted (C9, 2026-09-21)
  const askedWeek = /^\d{4}-\d{2}-\d{2}$/.test(sp.get('uke') ?? '')
    ? mondayOf(sp.get('uke') as string)
    : null;
  // ?mnd=2026-09 opens a month (Timer til lønn step 2 and Hjem link this way)
  const askedMonth = /^\d{4}-\d{2}$/.test(sp.get('mnd') ?? '') ? (sp.get('mnd') as string) : null;
  const span: 'maned' | 'uke' = askedWeek ? 'uke' : 'maned';
  const month = askedMonth ?? monthOf(askedWeek ?? today());
  const from = askedWeek ?? firstOf(month);
  const to = askedWeek ? addDays(askedWeek, 6) : lastOf(month);
  const shiftsQ = useShifts(addDays(from, -1), addDays(to, 1));
  const usersQ = useUsers();
  const absQ = useAbsences();
  const typesQ = useShiftTypes();
  const projectsOn = useProjectsOn();
  const projects = useProjects().data ?? [];
  const projectWord = useProjectWord();
  // ?prosjekt=<id>|uten: approval per project is a filter (plan 0033 decision 10); «Godkjenn N klare» follows it
  const prosjekt = projectsOn ? sp.get('prosjekt') : null;
  const w = useShiftWrites();
  const rules = tenantRules();
  const policy = breakPolicy(tenant);
  const tol = rules.toleranceMin ?? 15;
  const now = Date.now();

  // ?vakt=<id>: the registration open beside the list, in the address so Back, Forward and a refresh
  // come back to it, and a payroll check can open one record (#264)
  // Accept earlier Oversikt review links as well; new selections use the shared vakt parameter.
  const selectedId = sp.get('vakt') ?? sp.get('valgt');
  const openId = /^\d+$/.test(selectedId ?? '') ? Number(selectedId) : null;
  const setOpenId = (id: number | null) =>
    edit((n) => {
      n.delete('valgt');
      if (id == null) n.delete('vakt');
      else n.set('vakt', String(id));
    });
  const [drawer, setDrawer] = useState<number | null>(null);
  // what the button that opened the editor asked for: the missing clock times must be put in (issue #81)
  const [drawerFocus, setDrawerFocus] = useState<EditFocus | undefined>(undefined);
  const [adding, setAdding] = useState<DraftShift | null>(null);
  const [absDraft, setAbsDraft] = useState<AbsenceDraft | null>(null);
  const [picked, setPicked] = useState<Set<number>>(new Set()); // shift ids, list
  const [pickedRows, setPickedRows] = useState<Set<string>>(new Set()); // "monday:user", matrix
  const [layout, setLayout] = useState<'liste' | 'uker'>('liste');
  // ?ansatt=7 or ?ansatt=7,8,9: the people in view (Timer til lønn sends its batch this way)
  const scope = (sp.get('ansatt') ?? '')
    .split(',')
    .map(Number)
    .filter((n) => Number.isInteger(n) && n > 0);
  const who: number | 'alle' = scope.length === 1 ? (scope[0] as number) : 'alle';
  // ?team=Kjøkken: the team filter survives a correction, Back and a refresh, as the month does (#264)
  const team = sp.get('team') || 'alle';
  const setTeam = (v: string) => edit((n) => (v === 'alle' ? n.delete('team') : n.set('team', v)));
  const [needle, setNeedle] = useState('');
  const [more, setMore] = useState(false);
  const [page, setPage] = useState(0);
  const [confirm, setConfirm] = useState<{
    ids: number[];
    left: number;
  } | null>(null);

  const persons = useMemo(
    () =>
      (usersQ.data ?? [])
        .filter((u) => u.active_status)
        .sort((a, b) => fullName(a).localeCompare(fullName(b), 'nb')),
    [usersQ.data],
  );
  const personOf = useMemo(() => new Map((usersQ.data ?? []).map((u) => [u.id, u])), [usersQ.data]);
  const nameOf = (s: ApiShift) => fullName(s.user ?? personOf.get(s.user_id ?? -1) ?? { name: '' });
  const teamsOf = (uid: number | null) =>
    (personOf.get(uid ?? -1)?.teams ?? []).filter((x) => {
      const tid = (x as { tenant_id?: number }).tenant_id;
      return tid == null || tid === tenant?.id;
    });
  const shifts = useMemo(
    () =>
      (shiftsQ.data?.shifts ?? []).filter((s) => {
        const d = dateOf(s);
        return s.user_id != null && d >= from && d <= to;
      }),
    [shiftsQ.data, from, to],
  );
  const kindById = useMemo(() => new Map(shifts.map((s) => [s.id, kindOf(s, now, tol)])), [shifts, now, tol]);
  const kind = (s: ApiShift) => kindById.get(s.id) ?? 'future';
  const blocks = useMemo(() => blocksOf(shifts), [shifts]);
  const allCounts = useMemo(() => countKinds(shifts, now, tol), [shifts, now, tol]);

  // the people, team and search filters narrow everything below, counts included
  const people = [...new Set([...shifts.map((s) => s.user_id as number), ...scope])]
    .map((id) => {
      const s = shifts.find((x) => x.user_id === id);
      return {
        id,
        name: s ? nameOf(s) : fullName(personOf.get(id) ?? { name: `#${id}` }),
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name, 'nb'));
  const teamNames = [...new Set(people.flatMap((p) => teamsOf(p.id).map((x) => x.name)))].sort((a, b) =>
    a.localeCompare(b, 'nb'),
  );
  const q = needle.trim().toLowerCase();
  const worklist = worklistOf(sp.get('arbeidsliste'));
  const scoped = shifts.filter(
    (s) =>
      (!worklist || inWorklist(worklist, kind(s))) &&
      (!scope.length || scope.includes(s.user_id as number)) &&
      (prosjekt == null ||
        (prosjekt === 'uten' ? s.project_id == null : s.project_id === Number(prosjekt))) &&
      (team === 'alle' || teamsOf(s.user_id).some((x) => x.name === team)) &&
      (!q ||
        nameOf(s).toLowerCase().includes(q) ||
        (s.shiftType?.name ?? '').toLowerCase().includes(q) ||
        teamsOf(s.user_id).some((x) => x.name.toLowerCase().includes(q))),
  );
  const counts = countKinds(scoped, now, tol);

  const filter: Filter = ((): Filter => {
    const v = sp.get('vis') as Filter | null;
    if (v && FILTERS.includes(v)) return v;
    return allCounts.action > 0 ? 'handling' : 'klare';
  })();
  const clearPicks = () => {
    setPicked(new Set());
    setPickedRows(new Set());
    setOpenWeeks(null);
    setPage(0);
  };
  const setFilter = (f: Filter) => {
    edit((n) => {
      n.set('vis', f);
      n.delete('arbeidsliste');
    });
    clearPicks();
  };

  const weeks = useMemo(() => {
    const out: string[] = [];
    for (let m = mondayOf(from); m <= to; m = addDays(m, 7)) out.push(m);
    return out;
  }, [from, to]);
  const [openWeeks, setOpenWeeks] = useState<Set<string> | null>(null);
  // One week open by default (owner review at 100 rows, 2026-09-21): the week asked for, else the
  // first week holding something to do in this view, else the current week.
  const firstHit = scoped
    .filter((s) => (filter === 'alle' ? needsAction(kind(s)) : inFilter(filter, kind(s))))
    .map(dateOf)
    .sort()[0];
  const defaultWeek = askedWeek
    ? askedWeek
    : firstHit
      ? mondayOf(firstHit)
      : mondayOf(today() >= from && today() <= to ? today() : from);
  const isOpenWeek = (m: string) => (openWeeks ? openWeeks.has(m) : m === defaultWeek);
  const toggleWeek = (m: string, cur: boolean) => {
    const n = new Set(openWeeks ?? [defaultWeek]);
    cur ? n.delete(m) : n.add(m);
    setOpenWeeks(n);
  };
  const allOpen = weeks.every(isOpenWeek);

  const { approve, busy, dialog: absenceDialog } = useApproveMany(shifts);
  async function approveMany(ids: number[]) {
    await approve(ids);
    setPicked(new Set());
    setPickedRows(new Set());
  }

  const periodText =
    span === 'uke'
      ? `${T('Uke {n}', { n: isoWeek(from) })} · ${fmtDs(from)}–${fmtDs(to)}`
      : monthLabel(month);

  function exportCsv() {
    const ok = shifts.filter((s) => s.is_approved && s.user);
    if (!ok.length) return toast(T('Ingen godkjente timer å eksportere'));
    if (!policy) return toast(T('Bedriftens pauseinnstilling er ikke lastet ennå'));
    const csv = [
      ['Ansatt', 'Dato', 'Inn', 'Ut', T('Timer etter pause (hele vakten)'), 'Vakttype'],
      ...ok.map((s) => {
        const h = worked(s, policy);
        return [
          fullName(s.user ?? { name: '' }),
          dateOf(s),
          clock(s.punch_in_time),
          clock(s.punch_out_time),
          h == null ? '' : num(h),
          s.shiftType?.name ?? '',
        ];
      }),
    ]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(';'))
      .join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' }));
    a.download = span === 'uke' ? `timeliste-${from}-${to}.csv` : `timeliste-${month}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast(`${ok.length} godkjente registreringer eksportert`);
  }

  const unplanned = (typesQ.data ?? []).find((x) => /uten plan/i.test(x.name ?? '')) ?? typesQ.data?.[0];
  const addRegistration = () => {
    const d = today() >= from && today() <= to ? today() : to < today() ? to : from;
    setAdding({
      ...newDraft(d, who === 'alle' ? null : who, unplanned),
      published: true,
    });
  };
  // ?ny=registrering: the welcome overview's «Registrer timer» (#130) opens the editor once the types
  // are known, and the word leaves the address so a reload or Back does not open it again
  const askedNew = sp.get('ny') === 'registrering';
  // biome-ignore lint/correctness/useExhaustiveDependencies: once, when the types arrive
  useEffect(() => {
    if (!askedNew || !typesQ.data) return;
    if (can('timeliste.rette')) addRegistration();
    edit((n) => n.delete('ny'));
  }, [askedNew, typesQ.data]);
  const projectName = (s: ApiShift) =>
    s.project_id == null
      ? projectWord.none
      : (projects.find((p) => p.id === s.project_id)?.name ?? `${projectWord.one} ${s.project_id}`);

  // ── the list ──────────────────────────────────────────────────────────────────────────
  const listRows = scoped
    .filter((s) => inFilter(filter, kind(s)))
    .sort((a, b) => a.start_time.localeCompare(b.start_time) || nameOf(a).localeCompare(nameOf(b), 'nb'));
  const pages = Math.max(1, Math.ceil(listRows.length / PAGE));
  const pg = Math.min(page, pages - 1);
  const pageRows = listRows.slice(pg * PAGE, pg * PAGE + PAGE);
  // A day split on a project switch («Bytt oppdrag», plan 0033 decision 8) is two or more clocked
  // rows of one person on one day: the list shows one day line that opens to the rows.
  const splitDays = useMemo(() => {
    const m = new Map<string, ApiShift[]>();
    for (const s of listRows) {
      if (!s.punch_in_time) continue;
      const k = dayKeyOf(s);
      m.set(k, [...(m.get(k) ?? []), s]);
    }
    // a split is clocked out and in at the same second (splitParts, shared with «Uka»)
    for (const [k, v] of m) {
      const parts = splitParts(v);
      if (parts) m.set(k, parts);
      else m.delete(k);
    }
    return m;
  }, [listRows]);
  const [openDays, setOpenDays] = useState<Set<string>>(new Set());
  const ended = (s: ApiShift) => !['future', 'running'].includes(kind(s));
  const hours = listRows
    .filter(ended)
    .map((s) => worked(s, policy))
    .filter((h): h is number => h != null);
  const unresolved = listRows.filter((s) => needsAction(kind(s)) || blocks.has(s.id)).length;
  const readyInScope = scoped.filter((s) => bulkReady(kind(s), blocks.get(s.id)));
  const readyOnPage = pageRows.filter((s) => bulkReady(kind(s), blocks.get(s.id)));
  const pickOnPage = pageRows.filter((s) => pickable(kind(s), blocks.get(s.id)));
  // the open registration's page, once the list is known: a payroll link, Back or a refresh lands on
  // its row, not on page 1 (#264)
  const placed = useRef<number | null>(null);
  useEffect(() => {
    if (openId == null || placed.current === openId || !shiftsQ.data) return;
    placed.current = openId;
    const i = listRows.findIndex((s) => s.id === openId);
    if (i < 0) return;
    setPage(Math.floor(i / PAGE));
    requestAnimationFrame(() =>
      document.querySelector('[data-testid=review-row].sel')?.scrollIntoView?.({ block: 'nearest' }),
    );
  });
  // ?rett=ut|inn|begge, from a payroll check: the registration opens in the editor on that clock
  // field, once; the word leaves the address so Back does not open the editor again
  const askedFix = sp.get('rett');
  // biome-ignore lint/correctness/useExhaustiveDependencies: once, when the period has loaded
  useEffect(() => {
    if (!askedFix || !shiftsQ.data) return;
    if (openId != null && can('timeliste.rette') && shifts.some((s) => s.id === openId)) {
      setDrawerFocus({ punch: askedFix === 'ut' ? 'out' : askedFix === 'inn' ? 'in' : 'both' });
      setDrawer(openId);
    }
    edit((n) => n.delete('rett'));
  }, [askedFix, shiftsQ.data]);
  const allPicked = pickOnPage.length > 0 && pickOnPage.every((s) => picked.has(s.id));
  const somePicked = pickOnPage.some((s) => picked.has(s.id));

  const inOut = (s: ApiShift) => {
    const r = workedResult(s, policy);
    const od = outDay(s);
    return (
      <>
        <span className="tnum">
          {s.punch_in_time
            ? `${clock(s.punch_in_time)}–${s.punch_out_time ? clock(s.punch_out_time) : '?'}`
            : '–'}
        </span>
        {od && <small>{od}</small>}
        <small>
          {T('Plan')} {clock(s.start_time)}–{clock(s.end_time)}
          {r.status === 'ok' ? ` · ${T('{n} min trekk', { n: Math.round(r.deductedMinutes) })}` : ''}
        </small>
      </>
    );
  };

  const list = () => {
    if (!listRows.length)
      return (
        <Empty
          tone="done"
          title={filter === 'handling' ? T('Ingen vakter krever handling') : T('Ingenting her')}
        >
          {filter === 'handling'
            ? T('Alle avsluttede vakter er stemplet og innenfor planen, eller sett over.')
            : T('Ingen registreringer i dette utvalget.')}
        </Empty>
      );
    return (
      <>
        <table className="mt cards tlrev" data-testid="timer-review">
          <thead>
            <tr>
              <th>
                <span className="tlwho">
                  {mayApprove && pickOnPage.length > 0 && (
                    <input
                      type="checkbox"
                      aria-label={T('Velg alle som kan godkjennes på siden')}
                      checked={allPicked}
                      ref={(el) => {
                        if (el) el.indeterminate = somePicked && !allPicked;
                      }}
                      onChange={() => {
                        const n = new Set(picked);
                        for (const s of pickOnPage) allPicked ? n.delete(s.id) : n.add(s.id);
                        setPicked(n);
                      }}
                    />
                  )}
                  {T('Ansatt')}
                </span>
              </th>
              <th>{T('Dato')}</th>
              <th>{T('Inn–ut')}</th>
              <th className="num">{T('Arbeidstid')}</th>
              <th>{T('Status')}</th>
              {projectsOn && <th>{projectWord.one}</th>}
              <th>
                <span className="sr">{T('Handling')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {pageRows.flatMap((s) => {
              const render = () => {
                const k = kind(s);
                const b = blocks.get(s.id);
                const st = statusOf(k, b);
                const d = dateOf(s);
                const h = worked(s, policy);
                const sel = picked.has(s.id);
                const why = b ? null : k === 'deviation' ? deviationText(s, tol) : null;
                return (
                  <tr key={s.id} className={sel || openId === s.id ? 'sel' : ''} data-testid="review-row">
                    <td>
                      <span className="tlwho">
                        {mayApprove && pickable(k, b) ? (
                          <input
                            type="checkbox"
                            aria-label={T('Velg vakten {date} for {name}', {
                              date: fmtDs(d),
                              name: nameOf(s),
                            })}
                            checked={sel}
                            onChange={() => {
                              const n = new Set(picked);
                              sel ? n.delete(s.id) : n.add(s.id);
                              setPicked(n);
                            }}
                          />
                        ) : (
                          mayApprove &&
                          pickOnPage.length > 0 && <span className="tlnobox" aria-hidden="true" />
                        )}
                        <span>
                          <button type="button" className="mlink" onClick={() => setOpenId(s.id)}>
                            <PersonLabel id={s.user_id ?? 0} name={nameOf(s)} />
                          </button>
                          {/* said what it is: the team, else the shift type (owner 18.10: "not self explanatory") */}
                          <small>
                            {teamsOf(s.user_id).length
                              ? `${T('Team')}: ${teamsOf(s.user_id)
                                  .map((x) => x.name)
                                  .join(', ')}`
                              : s.shiftType?.name
                                ? `${T('Vakttype')}: ${s.shiftType.name}`
                                : ''}
                          </small>
                        </span>
                      </span>
                    </td>
                    <td data-k={T('Dato')}>
                      {DN[dow(d)]} {fmtDs(d)}
                    </td>
                    <td data-k={T('Inn–ut')}>{inOut(s)}</td>
                    <td className="num" data-k={T('Arbeidstid')}>
                      {h == null ? '–' : `${num(h)} t`}
                    </td>
                    <td>
                      <Status tone={st.tone}>{st.text}</Status>
                      {why && <small>{why}</small>}
                    </td>
                    {projectsOn && <td data-k={projectWord.one}>{projectName(s)}</td>}
                    <td className="tlact">
                      <Button
                        small
                        onClick={() => setOpenId(s.id)}
                        data-review-open={s.id}
                        aria-label={`${k === 'approved' ? T('Se registrering') : T('Se og vurder')}: ${nameOf(s)}, ${fmtDs(d)}`}
                      >
                        <Icon name="list" />
                        {k === 'approved' || k === 'future' ? T('Se registrering') : T('Se og vurder')}
                      </Button>
                    </td>
                  </tr>
                );
              };
              const key = dayKeyOf(s);
              const group = splitDays.get(key);
              if (!group?.includes(s)) return [render()];
              const open = openDays.has(key);
              if (group[0]?.id !== s.id) return open ? [render()] : [];
              const d = dateOf(s);
              const sum = group.map((x) => worked(x, policy)).filter((x): x is number => x != null);
              const first = clock((group[0] as ApiShift).punch_in_time);
              const lastOut = (group[group.length - 1] as ApiShift).punch_out_time;
              const last = lastOut ? clock(lastOut) : '?';
              const dayLine = (
                <tr key={`day-${key}`} className="tlday" data-testid="review-day">
                  <td>
                    <span className="tlwho">
                      {mayApprove && pickOnPage.length > 0 && <span className="tlnobox" aria-hidden="true" />}
                      <span>
                        <PersonLabel id={s.user_id ?? 0} name={nameOf(s)}>
                          <b>{nameOf(s)}</b>
                        </PersonLabel>
                        <small>
                          {T('{n} deler, byttet {what}', {
                            n: group.length,
                            what: projectWord.lower,
                          })}
                        </small>
                      </span>
                    </span>
                  </td>
                  <td data-k={T('Dato')}>
                    {DN[dow(d)]} {fmtDs(d)}
                  </td>
                  <td data-k={T('Inn–ut')}>
                    <span className="tnum">{`${first}–${last}`}</span>
                  </td>
                  <td className="num" data-k={T('Arbeidstid')}>
                    {sum.length ? `${num(sum.reduce((a, x) => a + x, 0))} t` : '–'}
                  </td>
                  <td>
                    <Status tone={group.every((x) => x.is_approved) ? 'ok' : 'neu'}>
                      {group.every((x) => x.is_approved)
                        ? T('Godkjent')
                        : T('{n} deler', { n: group.length })}
                    </Status>
                  </td>
                  {projectsOn && (
                    <td data-k={projectWord.one}>{[...new Set(group.map(projectName))].join(' → ')}</td>
                  )}
                  <td className="tlact">
                    <Button
                      small
                      aria-expanded={open}
                      onClick={() =>
                        setOpenDays((o) => {
                          const n = new Set(o);
                          open ? n.delete(key) : n.add(key);
                          return n;
                        })
                      }
                    >
                      {open ? T('Skjul delene') : T('Vis {n} deler', { n: group.length })}
                    </Button>
                  </td>
                </tr>
              );
              return [dayLine, ...(open ? [render()] : [])];
            })}
          </tbody>
        </table>
        <div className="mcf">
          {mayApprove && (
            <button
              type="button"
              className="mlink"
              disabled={!pageRows.some((s) => bulkReady(kind(s), blocks.get(s.id)))}
              onClick={() => setPicked(new Set([...picked, ...readyOnPage.map((s) => s.id)]))}
            >
              {T('Velg godkjennbare på siden')}
            </button>
          )}
          <span>{T('Side {a} av {b}', { a: pg + 1, b: pages })}</span>
          <span className="sp" />
          <Button small disabled={pg === 0} onClick={() => setPage(pg - 1)}>
            {T('Forrige')}
          </Button>
          <Button small disabled={pg + 1 >= pages} onClick={() => setPage(pg + 1)}>
            {T('Neste')}
          </Button>
        </div>
      </>
    );
  };

  // ── the matrix ────────────────────────────────────────────────────────────────────────
  const absOn = (uid: number, d: string) =>
    (absQ.data ?? []).find(
      (a) =>
        a.user_id === uid &&
        a.status === 'accepted' &&
        inRange(d, oslo(a.start_date)?.date ?? '', oslo(a.end_date)?.date ?? ''),
    );
  const cell = (cellShifts: ApiShift[], uid: number, d: string) => {
    if (!cellShifts.length) {
      const a = absOn(uid, d);
      return a ? <span className="tlc abs">{typeName(a.absence_type, T('Fravær'))}</span> : null;
    }
    const s = cellShifts[0] as ApiShift;
    const k = kind(s);
    const h = worked(s, policy);
    const person = rulePerson(personOf.get(uid) ?? (s.user as never), tenant);
    // a shift that has not ended is plan, not a problem: no flags until it is over
    const e =
      k === 'future' || k === 'running' ? [] : cellShifts.flatMap((x) => dayEntries(x, person, rules));
    const flag =
      e.length > 0 ? (
        <Tip text={e.map((x) => ruleText(t, x)).join(' · ')}>
          <span className={`tlflag ${worst(e)}`} aria-label={e.map((x) => ruleText(t, x)).join('. ')}>
            <Icon name={worst(e) === 'd' ? 'stop' : 'warn'} />
          </span>
        </Tip>
      ) : null;
    const hrs = h == null ? '–' : `${num(h)} t`;
    const body =
      k === 'approved' ? (
        <>
          <b>{hrs}</b>
          <small>{T('godkjent')}</small>
        </>
      ) : k === 'ready' ? (
        <b>{hrs}</b>
      ) : k === 'deviation' ? (
        <>
          <b>{hrs}</b>
          <small>{deviationShort(s, tol)}</small>
        </>
      ) : k === 'missing' ? (
        <>
          <b>–</b>
          <small>{T('Mangler stempling')}</small>
        </>
      ) : k === 'open' ? (
        <>
          <b>{clock(s.punch_in_time)} –</b>
          <small>{T('Ikke stemplet ut')}</small>
        </>
      ) : k === 'running' ? (
        <>
          <b>{s.punch_in_time ? `${clock(s.punch_in_time)} –` : T('plan')}</b>
          <small>{T('pågår')}</small>
        </>
      ) : (
        <>
          <b className="plan">
            {clock(s.start_time)}–{clock(s.end_time)}
          </b>
          <small>{T('planlagt')}</small>
        </>
      );
    return (
      <button
        type="button"
        className={`tlc ${k}`}
        onClick={() => setOpenId(s.id)}
        title={deviationText(s, tol) ?? undefined}
      >
        {body}
        {cellShifts.length > 1 && <small>+{cellShifts.length - 1}</small>}
        {flag}
      </button>
    );
  };

  const matrix = (f: Filter) => {
    const blocksOut = weeks.map((m) => {
      const days = Array.from({ length: 7 }, (_, i) => addDays(m, i)).filter((d) => d >= from && d <= to);
      const rowsOfWeek = persons
        .concat(
          // a person who has left still has the shifts they worked here
          [...new Set(scoped.map((s) => s.user_id as number))]
            .filter((id) => !persons.some((p) => p.id === id))
            .map((id) => personOf.get(id))
            .filter((u): u is NonNullable<typeof u> => !!u),
        )
        .map((u) => {
          const mine = scoped.filter((s) => s.user_id === u.id && mondayOf(dateOf(s)) === m);
          return { u, mine, kinds: mine.map(kind) };
        })
        // the filter chooses which people and weeks show; each keeps its whole week as context
        .filter((r) => r.mine.length > 0 && (f === 'alle' || r.kinds.some((k) => inFilter(f, k))));
      if (!rowsOfWeek.length) return null;
      const open = isOpenWeek(m);
      const miss = rowsOfWeek.flatMap((r) => r.kinds).filter((k) => k === 'missing' || k === 'open').length;
      const dev = rowsOfWeek.flatMap((r) => r.kinds).filter((k) => k === 'deviation').length;
      return (
        <div className="tlw" key={m}>
          <button type="button" className="tlwh" aria-expanded={open} onClick={() => toggleWeek(m, open)}>
            <span className={`chev${open ? ' on' : ''}`} aria-hidden="true">
              <Icon name="chevronDown" />
            </span>
            {T('Uke {n}', { n: isoWeek(m) })} · {fmtDs(m < from ? from : m)}–
            {fmtDs(addDays(m, 6) > to ? to : addDays(m, 6))}
            <span className="tlws">
              {[miss ? T('{n} uten stempling', { n: miss }) : '', dev ? T('{n} avvik', { n: dev }) : '']
                .filter(Boolean)
                .join(' · ')}
            </span>
          </button>
          {open && (
            <div className="tw">
              <table className="t tlm">
                <thead>
                  <tr>
                    <th className="cbx" aria-label={T('Velg')} />
                    <th>{T('Ansatt')}</th>
                    {days.map((d) => (
                      <th key={d} className={`c${dow(d) >= 5 ? ' we' : ''}`}>
                        {DN[dow(d)]} {fmtDs(d)}
                      </th>
                    ))}
                    <th className="n">{T('Timer')}</th>
                    <th className="st">{T('Godkjenning')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rowsOfWeek.map(({ u, mine, kinds }) => {
                    const key = `${m}:${u.id}`;
                    const done = kinds.filter((k) => k !== 'future' && k !== 'running');
                    const ready = mine.filter((s, i) => bulkReady(kinds[i] as Kind, blocks.get(s.id))).length;
                    const approved = kinds.filter((k) => k === 'approved').length;
                    const sel = pickedRows.has(key);
                    // hours only from finished shifts with both punches: "0 t" means zero worked,
                    // never a future or unpunched shift, which show a dash
                    const hrs = mine
                      .filter((_, i) => kinds[i] !== 'future' && kinds[i] !== 'running')
                      .map((s) => worked(s, policy))
                      .filter((h): h is number => h != null);
                    const status = !done.length
                      ? '–'
                      : approved === done.length
                        ? T('Godkjent')
                        : ready && ready === done.length - approved
                          ? ready === 1
                            ? T('Klar')
                            : T('Alle {n} klare', { n: ready })
                          : T('{a} av {b} klare', {
                              a: ready,
                              b: done.length - approved,
                            });
                    return (
                      <tr key={u.id} className={sel ? 'picked' : ''}>
                        <td className="cbx">
                          {mayApprove && ready > 0 && (
                            <input
                              type="checkbox"
                              aria-label={T('Velg {name}, uke {n}', {
                                name: fullName(u),
                                n: isoWeek(m),
                              })}
                              checked={sel}
                              onChange={() => {
                                const n = new Set(pickedRows);
                                sel ? n.delete(key) : n.add(key);
                                setPickedRows(n);
                              }}
                            />
                          )}
                        </td>
                        <td className="who">
                          <PersonLabel id={u.id} name={fullName(u)}>
                            <b>{fullName(u)}</b>
                          </PersonLabel>
                        </td>
                        {days.map((d) => (
                          <td key={d} className={`c${dow(d) >= 5 ? ' we' : ''}`}>
                            {cell(
                              mine.filter((s) => dateOf(s) === d),
                              u.id,
                              d,
                            )}
                          </td>
                        ))}
                        <td className="n">
                          {hrs.length ? <b>{num(hrs.reduce((a, h) => a + h, 0))} t</b> : '–'}
                        </td>
                        <td className="st">{status}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      );
    });
    return blocksOut.some(Boolean) ? (
      blocksOut
    ) : (
      <Empty tone="done">{T('Ingen vakter her i perioden.')}</Empty>
    );
  };

  // ── what a selection would approve, stated before anything is sent ────────────────────
  const pickedApprovable =
    layout === 'liste'
      ? scoped.filter((s) => picked.has(s.id) && pickable(kind(s), blocks.get(s.id)))
      : scoped.filter(
          (s) =>
            pickedRows.has(`${mondayOf(dateOf(s))}:${s.user_id}`) && bulkReady(kind(s), blocks.get(s.id)),
        );
  const pickedPending = scoped.filter(
    (s) =>
      pickedRows.has(`${mondayOf(dateOf(s))}:${s.user_id}`) &&
      !['approved', 'future', 'running'].includes(kind(s)),
  );

  const openShift = openId == null ? null : (shifts.find((s) => s.id === openId) ?? null);
  const drawerShift = drawer == null ? null : (shifts.find((s) => s.id === drawer) ?? null);
  const drawerPerson = drawerShift ? personOf.get(drawerShift.user_id ?? -1) : undefined;

  const detail = (s: ApiShift) => {
    const k = kind(s);
    const b = blocks.get(s.id);
    const st = statusOf(k, b);
    const d = dateOf(s);
    const h = worked(s, policy);
    const r = workedResult(s, policy);
    const person = personOf.get(s.user_id ?? -1);
    const entries = person && ended(s) ? dayEntries(s, rulePerson(person, tenant), rules) : [];
    const why =
      b === 'invalid'
        ? T('Utstemplingen er før eller lik innstemplingen. Rett tidene før vakten kan godkjennes.')
        : b === 'overlap'
          ? T('Ligger over en annen registrering for samme person. Rett eller slett den ene før godkjenning.')
          : k === 'missing'
            ? T('Vakten er over og ingen har stemplet. Legg inn tidene, eller registrer fravær.')
            : k === 'open'
              ? T('Stemplet inn, men aldri ut. Legg inn ut-tiden før vakten kan godkjennes.')
              : k === 'deviation'
                ? (deviationText(s, tol) ?? '')
                : null;
    const mayApproveThis = mayApprove && pickable(k, b);
    return (
      <DetailPanel
        label={T('Registrering')}
        restoreFocusTo={`[data-review-open="${s.id}"]`}
        title={<PersonLabel id={s.user_id ?? 0} name={nameOf(s)} />}
        sub={`${DN[dow(d)]} ${fmtDs(d)}${s.shiftType?.name ? ` · ${s.shiftType.name}` : ''}`}
        onClose={() => setOpenId(null)}
        footer={
          <>
            {mayApproveThis && (
              <Button tone="pri" disabled={busy} onClick={() => approveMany([s.id])}>
                <Icon name="check" />
                {T('Godkjenn')}
              </Button>
            )}
            <Button
              {...(mayApproveThis || k === 'approved' ? {} : { tone: 'pri' as const })}
              onClick={() => {
                setDrawerFocus(
                  k === 'open' ? { punch: 'out' } : k === 'missing' ? { punch: 'both' } : undefined,
                );
                setDrawer(s.id);
              }}
            >
              <Icon name="clock" />
              {k === 'missing'
                ? T('Legg inn tider')
                : k === 'open'
                  ? T('Legg inn ut-tid')
                  : T('Rett registrering')}
            </Button>
            <Menu
              tip={T('Flere valg')}
              trigger={
                <button type="button" className="more" aria-label={T('Flere valg')}>
                  <Icon name="more" />
                </button>
              }
              items={[
                ...(can('fravaer.registrere') && s.user_id != null
                  ? [
                      {
                        key: 'abs',
                        label: T('Registrer fravær i stedet'),
                        onSelect: () => setAbsDraft(newAbsenceDraft(s.user_id as number, d)),
                      },
                    ]
                  : []),
                {
                  key: 'open',
                  label: T('Åpne vakten'),
                  onSelect: () => {
                    setDrawerFocus(undefined);
                    setDrawer(s.id);
                  },
                },
              ]}
            />
          </>
        }
      >
        <p>
          <Status tone={st.tone}>{st.text}</Status>
        </p>
        {why && <p>{why}</p>}
        <Fact k={T('Planlagt')}>
          {clock(s.start_time)}–{clock(s.end_time)}
        </Fact>
        <Fact k={T('Stemplet')}>
          {s.punch_in_time
            ? `${clock(s.punch_in_time)}–${s.punch_out_time ? clock(s.punch_out_time) : '?'}`
            : '–'}
          {outDay(s) ? ` (${outDay(s)})` : ''}
        </Fact>
        <Fact k={T('Pause registrert')}>
          {s.lunch_in_time
            ? `${clock(s.lunch_in_time)}–${s.lunch_out_time ? clock(s.lunch_out_time) : '?'}`
            : '–'}
        </Fact>
        <Fact k={T('Pausetrekk')}>{r.status === 'ok' ? `${Math.round(r.deductedMinutes)} min` : '–'}</Fact>
        <Fact k={T('Arbeidstid')}>{h == null ? T('Ikke avklart') : `${num(h)} t`}</Fact>
        {projectsOn && <Fact k={projectWord.one}>{projectName(s)}</Fact>}
        {entries.length > 0 && (
          <ul className="tlrules">
            {entries.map((x) => (
              <li key={ruleText(t, x)}>{ruleText(t, x)}</li>
            ))}
          </ul>
        )}
        <p className="tlnote">
          {T('Retting og godkjenning er to handlinger. En rettet vakt må fortsatt godkjennes.')}{' '}
          {s.user_id != null && <Link to="/arbeidstid">{T('Se registreringene')} →</Link>}
        </p>
      </DetailPanel>
    );
  };

  const extras = [team !== 'alle' ? team : '', q ? `«${needle.trim()}»` : ''].filter(Boolean);
  const loading = shiftsQ.isPending || usersQ.isPending;

  return (
    <div className="timer">
      {absenceDialog}
      {confirm && (
        <div className="confirm">
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="timer-approve-title"
            data-testid="approve-confirm"
            onKeyDown={(e) => e.key === 'Escape' && setConfirm(null)}
          >
            <h3 id="timer-approve-title">{T('Godkjenne {what}?', { what: regs(confirm.ids.length) })}</h3>
            <p>{T('Godkjente timer går til lønnsgrunnlaget.')}</p>
            {confirm.left > 0 && (
              <p>
                {T(
                  '{n, plural, one {# registrering} other {# registreringer}} i listen er ikke med: de mangler stempling, har avvik, overlapper eller har ugyldig tid, og må ses over hver for seg.',
                  { n: confirm.left },
                )}
              </p>
            )}
            <div className="df">
              <Button onClick={() => setConfirm(null)}>{T('Avbryt')}</Button>
              <Button
                tone="pri"
                autoFocus
                disabled={busy}
                onClick={async () => {
                  const ids = confirm.ids;
                  setConfirm(null);
                  await approveMany(ids);
                }}
              >
                <Icon name="check" />
                {T('Godkjenn')} {vakter(confirm.ids.length)}
              </Button>
            </div>
          </div>
        </div>
      )}

      <TimerHead
        tab="gjennomgang"
        count={allCounts.action}
        onAdd={addRegistration}
        reports={[
          // not before the period has loaded: an early click said there was nothing to export
          {
            key: 'csv',
            label: T('Godkjente timer (CSV)'),
            onSelect: exportCsv,
            disabled: !shiftsQ.data,
          },
        ]}
      />
      <TabPanel tab="gjennomgang">
        {worklist && (
          <div className="mnote info" aria-live="polite">
            <div className="mnt">
              <b>{T(WORKLISTS[worklist])}</b>
              <span>
                {T('Arbeidsliste fra Oversikt')} · {shiftsQ.data ? regs(scoped.length) : T('Henter…')}
              </span>
            </div>
            <Button
              onClick={() => {
                edit((n) => n.delete('arbeidsliste'));
                clearPicks();
              }}
            >
              {T('Vis hele perioden')}
            </Button>
          </div>
        )}
        <div className="mbar" role="toolbar" aria-label={T('Filter')}>
          <label className="mf">
            {T('Periode')}
            <select
              className="inp"
              value={span}
              onChange={(e) => {
                const week = e.target.value === 'uke';
                edit((n) => {
                  if (week) {
                    n.set('uke', mondayOf(today() >= from && today() <= to ? today() : from));
                    n.delete('mnd');
                  } else {
                    n.delete('uke');
                    n.set('mnd', monthOf(from));
                  }
                });
                clearPicks();
              }}
            >
              <option value="maned">{T('Måned')}</option>
              <option value="uke">{T('Uke')}</option>
            </select>
          </label>
          <div className="mf">
            <span className="tllbl">{span === 'uke' ? T('Uke') : T('Måned')}</span>
            <PeriodControl
              label={span === 'uke' ? T('Uke') : T('Måned')}
              period={span === 'uke' ? 'week' : 'month'}
              month={span !== 'uke'}
              value={span === 'uke' ? from : month}
              onChange={(v) => {
                edit((n) => (span === 'uke' ? n.set('uke', mondayOf(v)) : n.set('mnd', v)));
                clearPicks();
              }}
              prevLabel={span === 'uke' ? T('Forrige uke') : T('Forrige måned')}
              nextLabel={span === 'uke' ? T('Neste uke') : T('Neste måned')}
              onPrev={() => {
                edit((n) =>
                  span === 'uke' ? n.set('uke', addDays(from, -7)) : n.set('mnd', stepMonth(month, -1)),
                );
                clearPicks();
              }}
              onNext={() => {
                edit((n) =>
                  span === 'uke' ? n.set('uke', addDays(from, 7)) : n.set('mnd', stepMonth(month, 1)),
                );
                clearPicks();
              }}
            >
              <span>{periodText}</span>
            </PeriodControl>
          </div>
          <label className="mf">
            {T('Ansatt')}
            <select
              className="inp"
              value={scope.length > 1 ? 'utvalg' : who}
              onChange={(e) => {
                const v = e.target.value;
                if (v !== 'utvalg') edit((n) => (v === 'alle' ? n.delete('ansatt') : n.set('ansatt', v)));
                clearPicks();
              }}
            >
              <option value="alle">{T('Alle ansatte')}</option>
              {scope.length > 1 && (
                <option value="utvalg">{T('{n} valgte ansatte', { n: scope.length })}</option>
              )}
              {people.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
          <label className="mf">
            {T('Status')}
            <select
              className="inp"
              value={filter}
              data-testid="timer-status"
              onChange={(e) => setFilter(e.target.value as Filter)}
            >
              {FILTERS.map((f) => {
                const n =
                  f === 'handling'
                    ? counts.action
                    : f === 'avvik'
                      ? counts.deviation
                      : f === 'uten'
                        ? counts.missing + counts.open
                        : f === 'klare'
                          ? counts.ready
                          : f === 'godkjent'
                            ? counts.approved
                            : null;
                return (
                  <option key={f} value={f}>
                    {T(FILTER_TEXT[f])}
                    {n != null ? ` (${n})` : ''}
                  </option>
                );
              })}
            </select>
          </label>
          {projectsOn && (
            <label className="mf">
              {projectWord.one}
              <select
                className="inp"
                data-testid="timer-project"
                value={prosjekt ?? 'alle'}
                onChange={(e) => {
                  const v = e.target.value;
                  edit((n) => (v === 'alle' ? n.delete('prosjekt') : n.set('prosjekt', v)));
                  clearPicks();
                }}
              >
                <option value="alle">{T('Alle')}</option>
                {projects.map((p) => (
                  <option key={p.id} value={String(p.id)}>
                    {projectLabel(p)}
                  </option>
                ))}
                <option value="uten">{projectWord.none}</option>
              </select>
            </label>
          )}
          <button
            type="button"
            className="btn tlmore"
            aria-expanded={more}
            aria-controls="timer-more-filters"
            onClick={() => setMore(!more)}
          >
            <Icon name="sliders" />
            {more ? T('Færre filtre') : T('Flere filtre')}
            {!more && extras.length ? ` (${extras.length})` : ''}
          </button>
        </div>
        {more && (
          <div className="mbar" id="timer-more-filters">
            {teamNames.length > 0 && (
              <label className="mf">
                {T('Team')}
                <select className="inp" value={team} onChange={(e) => setTeam(e.target.value)}>
                  <option value="alle">{T('Alle team')}</option>
                  {teamNames.map((x) => (
                    <option key={x}>{x}</option>
                  ))}
                </select>
              </label>
            )}
            <label className="mf">
              {T('Søk')}
              <input
                className="inp"
                type="search"
                placeholder={T('Navn, team eller vakttype')}
                value={needle}
                onChange={(e) => setNeedle(e.target.value)}
              />
            </label>
          </div>
        )}

        {shiftsQ.isError && (
          <LoadError
            what={T('vaktene')}
            detail={(shiftsQ.error as Error).message}
            onRetry={() => shiftsQ.refetch()}
          />
        )}

        {/* a registration asked for by the address that is not in this period: said, and the list shown */}
        {openId != null && shiftsQ.isSuccess && !openShift && (
          <Notice
            tone="info"
            title={T('Fant ikke registreringen i denne perioden')}
            action={
              <Button small onClick={() => setOpenId(null)}>
                {T('Lukk')}
              </Button>
            }
          >
            {T('Den kan være slettet, eller ligge i en annen måned. Listen viser hele utvalget.')}
          </Notice>
        )}
        {!loading && (
          <div className="tlsumline" data-testid="timer-summary">
            {scope.length > 1 && (
              <span className="tlscope" data-testid="timer-scope">
                {T('Utvalg: {n} ansatte', { n: scope.length })}{' '}
                <button
                  type="button"
                  className="mlink"
                  onClick={() => {
                    edit((n) => n.delete('ansatt'));
                    clearPicks();
                  }}
                >
                  {T('Fjern utvalg')}
                </button>
                {' · '}
              </span>
            )}
            <span className="sub">
              {regs(listRows.length)} {T('i listen')}
              {' · '}
              {hours.length
                ? `${num(hours.reduce((a, x) => a + x, 0))} t ${T('med avklart arbeidstid')}`
                : T('ingen avklart arbeidstid')}
              {unresolved > 0 && ` · ${T('{n} må avklares', { n: unresolved })}`}
            </span>
            <span className="sp" />
            {can('timerloenn.se') && (
              <Link className="btn" to={`/lonn?mnd=${month}`}>
                <Icon name="banknote" />
                {T('Åpne lønnsgrunnlag')}
              </Link>
            )}
            <button
              type="button"
              className="btn"
              aria-pressed={layout === 'uker'}
              onClick={() => {
                setLayout(layout === 'liste' ? 'uker' : 'liste');
                clearPicks();
              }}
            >
              <Icon name={layout === 'liste' ? 'grid' : 'list'} />
              {layout === 'liste' ? T('Grupper etter uke') : T('Vis registreringer som liste')}
            </button>
            {layout === 'uker' && (
              <Button small ghost onClick={() => setOpenWeeks(allOpen ? new Set() : new Set(weeks))}>
                {allOpen ? T('Lukk alle uker') : T('Utvid alle uker')}
              </Button>
            )}
            {mayApprove &&
              layout === 'liste' &&
              (filter === 'klare' ? (
                <Button
                  tone="pri"
                  small
                  disabled={busy || !readyOnPage.length}
                  onClick={() =>
                    setConfirm({
                      ids: readyOnPage.map((s) => s.id),
                      left: pageRows.length - readyOnPage.length,
                    })
                  }
                >
                  <Icon name="check" />{' '}
                  {T('Godkjenn {n, plural, one {# klar} other {# klare}} på siden', {
                    n: readyOnPage.length,
                  })}
                </Button>
              ) : (
                readyInScope.length > 0 && (
                  <Button small onClick={() => setFilter('klare')}>
                    <Icon name="check" />
                    {T('Se {n, plural, one {# klar} other {# klare}}', {
                      n: readyInScope.length,
                    })}
                  </Button>
                )
              ))}
          </div>
        )}

        <div className={`msplit${openShift ? ' open' : ''}`}>
          <section className="mcard tlcard">
            {loading ? <Skeleton rows={5} /> : layout === 'liste' ? list() : matrix(filter)}
          </section>
          {openShift && detail(openShift)}
        </div>

        {(picked.size > 0 || pickedRows.size > 0) && (
          <section className="selbar" aria-label={T('Valgte')}>
            <span aria-live="polite">
              {layout === 'liste'
                ? `${vakter(pickedApprovable.length)} ${T('valgt')}`
                : T('{p} ansatte valgt · {a} av {b} vakter kan godkjennes', {
                    p: new Set([...pickedRows].map((k) => k.split(':')[1])).size,
                    a: pickedApprovable.length,
                    b: pickedPending.length,
                  })}
            </span>
            <span className="sp" />
            <Button
              ghost
              onClick={() => {
                setPicked(new Set());
                setPickedRows(new Set());
              }}
            >
              {T('Nullstill')}
            </Button>
            <Button
              tone="pri"
              disabled={busy || !pickedApprovable.length}
              onClick={() => setConfirm({ ids: pickedApprovable.map((s) => s.id), left: 0 })}
            >
              <Icon name="check" />
              {T('Godkjenn')} {vakter(pickedApprovable.length)}
            </Button>
          </section>
        )}
      </TabPanel>
      <ShiftDrawer
        draft={adding ?? (drawerShift ? draftOf(drawerShift) : null)}
        focus={adding ? undefined : drawerFocus}
        tasks={adding ? undefined : drawerShift?.taskAssignments}
        entries={
          !adding && drawerShift && drawerPerson
            ? dayEntries(drawerShift, rulePerson(drawerPerson, tenant), rules)
            : []
        }
        types={typesQ.data ?? []}
        users={persons}
        onClose={() => {
          setDrawer(null);
          setAdding(null);
        }}
        onDelete={(id, reason) =>
          w.remove.mutateAsync({ id, reason }).then(
            () => {
              toast(T('Vakten er slettet'));
              setDrawer(null);
              setOpenId(null);
            },
            (e) => toast(errorText(e)),
          )
        }
      />
      <AbsenceDrawer draft={absDraft} shifts={shifts} onClose={() => setAbsDraft(null)} />
    </div>
  );
}
